/**
 * Coupled values of placed custom objects, as PURE functions (the adapter in
 * customSketchObjectType.ts reads the live properties, calls these and writes
 * the answers back): step ↔ number of steps (`stepSyncs`), the linked depth
 * (`depthLink`) and handles that set params (`handles.depth[].assign`).
 *
 * Contract (the built-in stairs' / cabinets'): every write is skipped when the
 * value already agrees, and each write re-enters the sync with its own key —
 * that is what makes the mutual updates settle.
 */

import type { ObjectDefinitionSpec, ObjectParamAssignment } from 'shared-types';
import { evaluationVars, expressionValue, linearRepeatCount, linearRepeatRunLength, type CustomParamValues } from './evaluateCustomGlyph';

/** Two coupled values never fight below this difference. */
export const SYNC_EPS = 1e-4;

/** What a placed object currently holds (params resolved: defaults filled in). */
export interface LiveObjectState {
  width: number;
  depth: number;
  params: CustomParamValues;
}

/** One property write: a param key or the shared `'depth'`. */
export interface SyncWrite {
  key: string;
  value: number;
  /** A count written by an `exactStep` sync: its echo must not re-even the step. */
  countEcho?: boolean;
}

const numberParam = (params: CustomParamValues, key: string): number => {
  const v = params[key];
  return typeof v === 'number' ? v : 0;
};

/** All assignments of a list evaluated against the same values, then only the ones that change something. */
export function assignmentWrites(assignments: readonly ObjectParamAssignment[], vars: Readonly<Record<string, number>>, params: CustomParamValues): SyncWrite[] {
  return assignments
    .map((a) => ({ key: a.param, value: expressionValue(a.expr, vars) }))
    .filter((w) => Math.abs(numberParam(params, w.key) - w.value) > SYNC_EPS);
}

/**
 * The writes one change calls for. `countEcho` is true while the adapter
 * writes a count answered with `countEcho` (its re-entrant notification).
 */
export function customSyncWrites(spec: ObjectDefinitionSpec, changedKey: string, live: LiveObjectState, countEcho: boolean): SyncWrite[] {
  const vars = evaluationVars(spec, live.width, live.depth, live.params);
  const writes: SyncWrite[] = [];

  const link = spec.depthLink;
  if (link) {
    if (changedKey === 'depth') {
      // The depth was resized directly: move the params so the link lands on it (their writes come back here).
      if (link.onResize) return assignmentWrites(link.onResize, vars, live.params);
    } else {
      const want = expressionValue(link.expr, vars);
      if (want > 0 && Math.abs(live.depth - want) > SYNC_EPS) writes.push({ key: 'depth', value: want });
    }
  }

  for (const sync of spec.stepSyncs ?? []) {
    const run = expressionValue(sync.run, vars);
    if (run <= 0) continue;
    const minCount = Math.max(1, sync.minCount ?? 1);
    if (changedKey === sync.count) {
      // A count that merely follows a resize keeps an exact step; only a direct edit redistributes the run.
      if (countEcho && sync.exactStep) continue;
      const count = Math.max(minCount, Math.round(numberParam(live.params, sync.count) || minCount));
      const step = run / count;
      if (Math.abs(numberParam(live.params, sync.step) - step) > SYNC_EPS) writes.push({ key: sync.step, value: step });
    } else {
      const step = Math.max(0.05, numberParam(live.params, sync.step) || 0.25);
      const count = Math.max(minCount, Math.round(run / step));
      if (numberParam(live.params, sync.count) !== count) writes.push({ key: sync.count, value: count, ...(sync.exactStep ? { countEcho: true } : {}) });
    }
  }
  return writes;
}

/**
 * A custom depth handle drag: the param writes for the cursor's `pulled`
 * distance, and the depth that results (the link's value once the writes
 * are in, else `pulled` itself).
 */
export function depthHandleDrag(spec: ObjectDefinitionSpec, assignments: readonly ObjectParamAssignment[], live: LiveObjectState, pulled: number): { writes: SyncWrite[]; depth: number } {
  const vars = { ...evaluationVars(spec, live.width, live.depth, live.params), pulled };
  const writes = assignmentWrites(assignments, vars, live.params);
  if (!spec.depthLink) return { writes, depth: pulled };
  const after: Record<string, CustomParamValues[string]> = { ...live.params };
  for (const w of writes) after[w.key] = w.value;
  return { writes, depth: expressionValue(spec.depthLink.expr, evaluationVars(spec, live.width, live.depth, after)) };
}

/**
 * The linear repeats' own pitch ↔ count pairs (both param-bound): the pitch
 * is the authored invariant, a direct count edit redistributes the run — the
 * straight stair's contract. `countEcho` = a count this sync just wrote.
 */
export function repeatSyncWrites(spec: ObjectDefinitionSpec, changedKey: string, live: LiveObjectState, countEcho: boolean): SyncWrite[] {
  const writes: SyncWrite[] = [];
  for (const group of spec.groups) {
    const repeat = group.repeat;
    if (repeat?.kind !== 'linear' || !repeat.pitch || !repeat.count || !('param' in repeat.pitch) || !('param' in repeat.count)) continue;
    const pitchKey = repeat.pitch.param;
    const countKey = repeat.count.param;
    const runKey = repeat.axis === 'u' ? 'width' : 'depth';
    if (changedKey !== runKey && changedKey !== pitchKey && changedKey !== countKey) continue;
    const run = linearRepeatRunLength(spec, group, live.width, live.depth, live.params);
    if (run <= 0) continue;
    if (changedKey === countKey) {
      if (countEcho) continue;
      const count = Math.max(1, Math.round(numberParam(live.params, countKey) || 1));
      const pitch = run / count;
      if (Math.abs(numberParam(live.params, pitchKey) - pitch) > SYNC_EPS) writes.push({ key: pitchKey, value: pitch });
    } else {
      const count = linearRepeatCount({ ...repeat, count: undefined }, run, { [pitchKey]: numberParam(live.params, pitchKey) });
      if (numberParam(live.params, countKey) !== count) writes.push({ key: countKey, value: count, countEcho: true });
    }
  }
  return writes;
}

/** Recursion guard of {@link settleSync} (a cascade never needs more than a few levels). */
const MAX_SYNC_DEPTH = 24;

/**
 * What a placed object ends up holding after `changedKey` changed — the
 * app's synchronous notify cascade replayed on plain values: each sync
 * write is applied when it changes something and re-enters the sync with its
 * own key, exactly as the property service does (the editor's Test mode
 * uses this so a test instance behaves like a placed one).
 */
export function settleSync(spec: ObjectDefinitionSpec, live: LiveObjectState, changedKey: string): LiveObjectState {
  let state: LiveObjectState = { ...live, params: { ...live.params } };
  const current = (key: string): number => (key === 'depth' ? state.depth : key === 'width' ? state.width : numberParam(state.params, key));
  const write = (w: SyncWrite, depth: number) => {
    if (Math.abs(current(w.key) - w.value) <= 1e-12) return;
    state = w.key === 'depth' ? { ...state, depth: w.value } : w.key === 'width' ? { ...state, width: w.value } : { ...state, params: { ...state.params, [w.key]: w.value } };
    notify(w.key, w.countEcho === true, depth + 1);
  };
  const notify = (key: string, echo: boolean, depth: number) => {
    if (depth > MAX_SYNC_DEPTH) return;
    // The adapter runs the step syncs + linked depth first, then the repeats (each from the values at that moment).
    for (const w of customSyncWrites(spec, key, state, echo)) write(w, depth);
    for (const w of repeatSyncWrites(spec, key, state, echo)) write(w, depth);
  };
  notify(changedKey, false, 0);
  return state;
}
